// subscription.factory.ts
import {
  Product,
  ProductShortName,
  RouteTypes,
  SubscriptionName,
  SubscriptionType,
} from '@app/enums/_index';

import { LMSSubscription } from '@app/models/_index';
import { environment } from '@environments/environment';
import { Factory } from './_factory';

/**
 * Create Subscription Factory used by application
 * Creates instances of Subscription Items.
 */
export class SubscriptionFactory implements Factory<LMSSubscription> {
  userSubscriptions: LMSSubscription[] = [];

  constructor() {
    this.build();
  }

  /**
   * Build static Learning Subscription items array for a user
   */
  build(): void {
    this.userSubscriptions.push(
      new LMSSubscription(
        environment.beyond.genesys_cloud.subscription_id,
        '',
        SubscriptionName.GENESYS_CLOUD_SUBSCRIPTION,
        'You have access to all available training for Genesys Cloud and any new content released during the year of your active subscription.  In addition, receive vouchers to test for any Genesys Cloud certifications.',
        SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION,
        SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION,
        ProductShortName.GENESYS_CLOUD,
        1,
        ProductShortName.GENESYS_CLOUD,
        ProductShortName.GENESYS_CLOUD,
        [],
        '',
        'Technical Training for System Administrators, Developers and Business Users.',
        environment.beyond.genesys_cloud.featureFlags.canShowProductInMenu,
        environment.beyond.genesys_cloud.featureFlags.canShowProduct,
        Product.GENESYS_CLOUD_DISPLAY_NAME
      ),
      new LMSSubscription(
        -1,
        '',
        SubscriptionName.AI_ESSENTIAL_SUBSCRIPTION,
        'You have access to all AI Essentials training across every role-based learning path, including any new content released during your active subscription. The program also includes embedded certifications within the System Administrator, Developer, and Business User learning paths.',
        SubscriptionType.AI_ESSENTIAL_SUBSCRIPTION,
        '',
        ProductShortName.AI_ESSENTIALS,
        2,
        ProductShortName.AI_ESSENTIALS,
        ProductShortName.AI_ESSENTIALS,
        [],
        '',
        'AI Capability Development for System Administrators, Developers and Business Users.',
        environment.beyond.beyondcx.featureFlags.canShowProductInMenu,
        environment.beyond.beyondcx.featureFlags.canShowProduct,
        Product.AI_ESSENTIALS_DISPLAY_NAME
      ),
      new LMSSubscription(
        -1,
        '',
        SubscriptionName.AGENT_PLATFORM_SUBSCRIPTION,
        'An Agent Platform (AP) Subscription for Genesys Cloud provides access to top agent courses, certification exams, and any new content released during the year of an active subscription.',
        SubscriptionType.AGENT_PLATFORM_SUBSCRIPTION,
        '',
        ProductShortName.AGENT_PLATFORM,
        3,
        ProductShortName.AGENT_PLATFORM,
        ProductShortName.AGENT_PLATFORM,
        [],
        '',
        'Product Training for Contact Center Users on Genesys Cloud.',
        environment.beyond.agent_platform.featureFlags.canShowProductInMenu,
        environment.beyond.agent_platform.featureFlags.canShowProduct,
        Product.AGENT_PLATFORM_DISPLAY_NAME_2
      ),
      new LMSSubscription(
        environment.beyond.engage.subscription_id,
        '',
        SubscriptionName.GENESYS_ENGAGE_SUBSCRIPTION,
        'You have access to all available training for Genesys Engage on-premises and any new content released during the year of your active subscription.  In addition, receive vouchers to test for any Genesys Engage on-premises certifications.',
        SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION,
        SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION,
        ProductShortName.GENESYS_ENGAGE,
        5,
        ProductShortName.GENESYS_ENGAGE,
        ProductShortName.GENESYS_ENGAGE,
        [],
        '',
        '',
        environment.beyond.engage.featureFlags.canShowProductInMenu,
        environment.beyond.engage.featureFlags.canShowProduct,
        Product.GENESYS_ENGAGE_DISPLAY_NAME
      ),
      new LMSSubscription(
        environment.beyond.pureconnect.subscription_id,
        '',
        SubscriptionName.PURECONNECT_SUBSCRIPTION,
        'You have access to more than 25 of the top courses currently available for PureConnect, as well as any new content released during the year of your active subscription.  In addition, receive vouchers to test for any PureConnect certifications.',
        SubscriptionType.PURECONNECT_SUBSCRIPTION,
        SubscriptionType.PURECONNECT_SUBSCRIPTION,
        ProductShortName.PURE_CONNECT,
        6,
        ProductShortName.PURE_CONNECT,
        ProductShortName.PURE_CONNECT,
        [],
        '',
        '',
        environment.beyond.pureconnect.featureFlags.canShowProductInMenu,
        environment.beyond.pureconnect.featureFlags.canShowProduct,
        Product.PURE_CONNECT_DISPLAY_NAME
      ),
      new LMSSubscription(
        environment.beyond.genesys_cloud_partner_digital.subscription_id,
        '',
        SubscriptionName.GENESYS_CLOUD_PARTNER_DIGITAL_SUBSCRIPTION,
        'Please click on the link above to access your Subscription via our Learning plan page.',
        `${environment.docebo.url}/learn/lp/${environment.beyond.genesys_cloud_partner_digital.subscription_id}`,
        SubscriptionType.GENESYS_CLOUD_PARTNER_DIGITAL_SUBSCRIPTION,
        ProductShortName.GENESYS_CLOUD,
        8,
        ProductShortName.GENESYS_CLOUD_PARTNER,
        ProductShortName.GENESYS_CLOUD_PARTNER,
        [],
        RouteTypes.EXTERNAL,
        '',
        false,
        false
      ),
      new LMSSubscription(
        environment.beyond.genesys_cloud_partner_digital_plus.subscription_id,
        '',
        SubscriptionName.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS_SUBSCRIPTION,
        'Please click on the link above to access your Subscription via our Learning plan page.',
        `${environment.docebo.url}/learn/lp/${environment.beyond.genesys_cloud_partner_digital_plus.subscription_id}`,
        SubscriptionType.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS,
        ProductShortName.GENESYS_CLOUD, // Icon name
        10,
        ProductShortName.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS,
        ProductShortName.GENESYS_CLOUD_PARTNER_DIGITAL_PLUS,
        [],
        RouteTypes.EXTERNAL,
        '',
        false,
        false
      ),
      new LMSSubscription(
        environment.beyond.genesys_cloud_partner_premier.subscription_id,
        '',
        SubscriptionName.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION,
        'This Learning subscription includes access to all available training for Genesys Cloud in addition to any new content released during the year of your active subscription. You will also receive vouchers to be able to test for any Genesys Cloud certifications.',
        SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION,
        SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION,
        ProductShortName.GENESYS_CLOUD,
        12,
        ProductShortName.GENESYS_CLOUD_PREMIER_PARTNER,
        ProductShortName.GENESYS_CLOUD_PREMIER_PARTNER,
        [],
        '',
        '',
        false,
        false
      ),
      new LMSSubscription(
        environment.beyond.genesys_cloud_trial.subscription_id,
        '',
        SubscriptionName.GENESYS_CLOUD_TRIAL_SUBSCRIPTION,
        'This trial learning subscription includes access to Genesys Cloud eLearning and webinars along with the limited selection of courses that were listed in your enrollment email.',
        SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION,
        SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION,
        ProductShortName.GENESYS_CLOUD,
        14,
        ProductShortName.GENESYS_CLOUD_TRIAL,
        ProductShortName.GENESYS_CLOUD_TRIAL,
        [],
        '',
        '',
        false,
        false
      ),
      new LMSSubscription(
        -1,
        '',
        SubscriptionName.AGENT_PLATFORM_AP_ENGLISH,
        'An Agent Platform (AP) Subscription for Genesys Cloud provides access to top agent courses, certification exams, and any new content released during the year of an active subscription in English.',
        SubscriptionType.AP_ENGLISH,
        SubscriptionType.AP_ENGLISH,
        ProductShortName.AP_ENGLISH,
        1,
        ProductShortName.AP_ENGLISH,
        ProductShortName.AP_ENGLISH,
        [],
        '',
        'Product Training for Contact Center Users on Genesys Cloud in English.',
        environment.beyond.agent_platform_english.featureFlags.canShowProductInMenu,
        environment.beyond.agent_platform_english.featureFlags.canShowProduct,
        Product.AGENT_PLATFORM_DISPLAY_NAME_ENGLISH
      ),
      new LMSSubscription(
        -1,
        '',
        SubscriptionName.AGENT_PLATFORM_AP_SPANISH,
        'An Agent Platform (AP) Subscription for Genesys Cloud provides access to top agent courses, certification exams, and any new content released during the year of an active subscription in Spanish.',
        SubscriptionType.AP_SPANISH,
        SubscriptionType.AP_SPANISH,
        ProductShortName.AP_SPANISH,
        1,
        ProductShortName.AP_SPANISH,
        ProductShortName.AP_SPANISH,
        [],
        '',
        'Product Training for Contact Center Users on Genesys Cloud in Spanish.',
        environment.beyond.agent_platform_spanish.featureFlags.canShowProductInMenu,
        environment.beyond.agent_platform_spanish.featureFlags.canShowProduct,
        Product.AGENT_PLATFORM_DISPLAY_NAME_SPANISH
      ),
      new LMSSubscription(
        -1,
        '',
        SubscriptionName.AGENT_PLATFORM_AP_PORTUGUESE,
        'An Agent Platform (AP) Subscription for Genesys Cloud provides access to top agent courses, certification exams, and any new content released during the year of an active subscription in Portuguese.',
        SubscriptionType.AP_PORTUGUESE,
        SubscriptionType.AP_PORTUGUESE,
        ProductShortName.AP_PORTUGUESE,
        1,
        ProductShortName.AP_PORTUGUESE,
        ProductShortName.AP_PORTUGUESE,
        [],
        '',
        'Product Training for Contact Center Users on Genesys Cloud in Portuguese.',
        environment.beyond.agent_platform_portuguese.featureFlags.canShowProductInMenu,
        environment.beyond.agent_platform_portuguese.featureFlags.canShowProduct,
        Product.AGENT_PLATFORM_DISPLAY_NAME_PORTUGUESE
      )
    );
  }

  /**
   * Return immutable or copy of Subscription collection
   * @returns - a slice or copy of thes static collection
   */
  getCollection(): LMSSubscription[] {
    if (this.userSubscriptions && this.userSubscriptions.length === 0) {
      this.build();
    }
    return this.userSubscriptions.slice();
  }

  /**
   * Return immutable or copy of Subscription item by subscription shortcut name
   * @param subscriptionShortCutName - subscription name string
   * @returns - returns a LMSSubscription by subscription name slug
   */
  getSubscription(subscriptionShortCutName: string): LMSSubscription {
    return this.getCollection().find(
      (item: LMSSubscription) => item.shortcutName === subscriptionShortCutName
    );
  }
}
